import { Injectable } from '@angular/core';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { NgxCaptureService } from 'ngx-capture';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, switchAll, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from 'src/environments/environment';

import type { MediaConnection, Peer } from 'peerjs';
import { CheckPlatformService } from '../helpers/check-platform.service';
import { Crypto } from '../helpers/encryption-utils';
import { TokenService } from '../helpers/token.service';

export const WS_ENDPOINT = environment.wsEndpoint;
@Injectable({
  providedIn: 'root',
})
export class WS {
  public socket$: WebSocketSubject<any> | null = null;
  private messagesSubject$ = new Subject();
  public socketError = new Subject();
  public messages$ = this.messagesSubject$.pipe(
    switchAll(),
    catchError(e => {
      throw e;
    })
  );
  private messageService = null;
  notifications = Observable;
  public isProjectStatusLocked = new Subject();
  public onsiteChat = new Subject();
  public reconnectChat = new Subject();
  public Status = new Subject();
  private notifications2 = new Subject();
  private email_refresh = new Subject();
  private email_sync_progress = new Subject<Record<string, unknown>>();
  private calendar_refresh = new Subject();
  public AInotifications = new Subject();
  private imgStream = new Subject();
  private dynamicSyncBanner = new Subject();
  // Remmi Chat frames (type: 'chat'). Kept on its own subject rather than folded into
  // `onsiteChat` — that one is now sale-board / matrix / notice-board activity, and mixing the
  // two would make every chat message wake those screens up.
  private chatEvents = new Subject<any>();

  // Auto-reconnect state (server runs a heartbeat and terminates dead sockets).
  private reconnectAttempts = 0;
  private reconnectTimer: any = null;
  private manualClose = false;
  // The real socket state, driven by the open/close observers. `socket$.closed` is the RxJS
  // Subject's flag and stays false after the underlying socket has errored or closed.
  private open = false;
  private connecting = false;
  // Bumped per socket so a late close event from a socket we already replaced cannot flip `open`
  // or schedule a second reconnect for the new one.
  private socketGeneration = 0;
  public get isOpen(): boolean {
    return this.open;
  }

  public onSiteChatInfo: any = {};
  public onSiteChatLiveUsers: any = {};
  // conditionally used data subject to use outside of this service, directly
  private data = new Subject();
  public getData() {
    return this.data;
  }

  private callStatus = new Subject();
  public getCallStatus() {
    return this.callStatus;
  }
  private screen: any = null;
  private capture: any;
  private Client: any;

  private PeerId: any;
  public getPeerId() {
    return this.PeerId;
  }
  constructor(
    private captureService: NgxCaptureService,
    private checkPlatformService: CheckPlatformService,
    private localStorageService: LocalStorageService,
    private tokenService: TokenService,
    private remmiConfirm: RemmiConfirmService
  ) {}
  public selectScreen(screen) {
    this.screen = screen;
  }

  /**
   * Stored `uid`, falling back to the id inside the access token.
   *
   * The fallback is the point: `uid` is wiped on logout and only rewritten once `/me` answers, so
   * a connect attempted right after login finds it empty.
   */
  public resolveUserId(): string | null {
    return this.getUserId() || this.tokenService.getUserIdFromToken();
  }

  // Services are optional so a later connect() (e.g. once `/me` answers) can reuse the ones the
  // layout handed over instead of replacing them with a look-alike from another component.
  // The third argument is ignored: confirms now go through RemmiConfirmService. It is kept so existing
  // callers that still pass a PrimeNG ConfirmationService keep compiling.
  public async connect(user, messageService?, _confirmationService?): Promise<any> {
    this.messageService = messageService ?? this.messageService;

    // A valid Mongo user id is required. Empty/invalid ids are rejected by the
    // server with close code 1008, so don't even attempt to connect.
    if (!user) {
      this.Status.next('Disconnected');
      return;
    }

    // A fresh connect() (e.g. login) is an intentional (re)start — allow reconnects again.
    this.manualClose = false;

    // Already open (or opening) for this user: nothing to do. Safe to call from several places.
    if (this.Client === user && (this.open || this.connecting)) {
      return;
    }

    // Identity changed without a reload — the old socket belongs to someone else.
    if (this.Client !== user) {
      this.closeSocketConnection();
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.establishSocket(user);
  }

  /** Re-establish if we are logged in and have no open socket. Safe to call often. */
  public ensureConnected(): void {
    if (this.manualClose || this.open || this.connecting || this.reconnectTimer) {
      return;
    }
    const user = this.resolveUserId();
    if (user) {
      this.establishSocket(user);
    }
  }

  /** Drop the current socket and open a fresh one (the server says it holds none for us). */
  public forceReconnect(): void {
    if (this.manualClose) {
      return;
    }
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.closeSocketConnection();
    this.open = false;
    this.connecting = false;
    this.ensureConnected();
  }

  // Creates the socket, wires handlers and (re)runs the handshake. Reused by the
  // initial connect() and by the auto-reconnect timer.
  private establishSocket(user): void {
    if (this.manualClose) {
      return;
    }
    const generation = ++this.socketGeneration;
    this.Client = user;
    this.open = false;
    this.connecting = true;
    this.socket$ = webSocket({
      url: WS_ENDPOINT + `?userid=${user}`,
      openObserver: {
        next: () => {
          if (generation !== this.socketGeneration) {
            return;
          }
          this.open = true;
          this.connecting = false;
          this.reconnectAttempts = 0; // reset backoff on a successful open
          console.log('%c connected to server via web socket', 'color: #32CD32');
          this.Status.next('Connected');
        },
      },
      closeObserver: {
        next: (closeEvent: CloseEvent) => {
          if (generation !== this.socketGeneration) {
            return;
          }
          this.open = false;
          this.connecting = false;
          this.onSocketClose(closeEvent, user);
        },
      },
    });

    this.socket$.subscribe({
      next: data => this.handleResponse(data),
      error: err => {
        this.socketError.next(err);
      },
      complete: () => {
        console.log('connected to server via web socket');
      },
    });

    this.socket$.next({ action: 'HandShak', type: '', data: user });

    this.socket$.next({ action: '/notifications', type: 'api', post: { per_page: 'all' }, user: user });

    const messages = this.socket$.pipe(
      tap({
        error: error => console.log(error),
      }),
      catchError(_ => EMPTY)
    );
    this.messagesSubject$.next(messages);
    this.reconnectChat.next('reconnected');
  }

  // Auto-reconnect with exponential backoff (capped at 30s). The server heartbeat
  // terminates dead/half-open sockets, so the client must re-establish on close.
  private onSocketClose(closeEvent: CloseEvent, user): void {
    this.Status.next('Disconnected');

    if (this.manualClose) {
      return; // intentional close (logout / destroy) — do not reconnect
    }
    if (closeEvent?.code === 1008) {
      // invalid userId — reconnecting would just fail again
      console.error('%c WebSocket closed: invalid userId (1008). Not reconnecting.', 'color: #ff4500');
      return;
    }

    // ±30% jitter so every open tab doesn't reconnect (and re-fetch notifications) in the same second after a server restart.
    const backoff = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempts));
    const wait = Math.round(backoff * (0.7 + Math.random() * 0.6));
    this.reconnectAttempts += 1;
    console.log(`%c WebSocket closed (code ${closeEvent?.code}). Reconnecting in ${wait}ms`, 'color: #ffa500');

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manualClose) {
        this.establishSocket(user);
      }
    }, wait);
  }
  private handleResponse(data) {
    if (data.type == 'info') {
      if (data.action == 'online-users') {
        this.onSiteChatLiveUsers = [];
        this.onSiteChatLiveUsers = data.data;
        this.data.next(data);
        this.onsiteChat.next(data);
      }
    }

    if (data.type == 'notification') {
      this.notifications2.next(data.data);
    }
    if (data.type == 'email-refresh') {
      this.email_refresh.next(true);
    }
    if (data.type == 'email-sync-progress') {
      this.email_sync_progress.next(data);
    }
    if (data.type == 'calendar-refresh') {
      this.calendar_refresh.next(true);
    }
    if (data.type == 'project-lock-status') {
      this.isProjectStatusLocked.next(data.data);
    }
    if (data.type == 'AI-notification') {
      this.AInotifications.next(data.data);
    }
    if (data.type == 'dynamic-sync-banner') {
      this.dynamicSyncBanner.next(data);
    }
    if (data.type == 'chat') {
      this.chatEvents.next(data);
    }
    if (data.type == 'reminder') {
      this.messageService.clear();
      data.data.forEach(itm => {
        let title = itm?.summary;
        let description = itm?.description;

        if (title == null || title == '') title = itm?.title;

        if (description == null || description == '') {
          description = itm?.notes;
        }
      });
    }

    if (data.type == 'stream') {
      if (data.action == 'screenCapture') {
        this.imgStream.next(data.data);
      }
    }

    if (data.type == 'peerRequest') {
      console.info('peer call', data);
      if (data.action == 'enableCallAnswer') {
        console.info('enable call');
        this.remmiConfirm
          .ask({ title: 'Accept Screen Share?', message: 'Accept the incoming screen share request?', confirmLabel: 'Accept' })
          .subscribe(isConfirmed => isConfirmed && this.enableCallAnswer(data.fromuser));
      } else if (data.action == 'establishMediaCall') {
        this.remmiConfirm
          .ask({ title: 'View Shared Screen?', message: 'Display the incoming shared screen?', confirmLabel: 'View' })
          .subscribe(isConfirmed => isConfirmed && this.establishMediaCall(data.fromuser));
      } else if (data.action == 'endcall') {
        this.remmiConfirm
          .ask({ title: 'Session Closed', message: 'The screen share session has ended.', confirmLabel: 'OK', hideCancel: true })
          .subscribe(isConfirmed => isConfirmed && this.closeMediaCall());
        this.closeMediaCall();
      }
    }

    if (
      data.type == 'onsite-chat' ||
      data.type == 'notice-board-activity' ||
      data.type == 'matrix' ||
      data.type == 'unread-reminder' ||
      data.type === 'matrix-request-status'
    ) {
      this.onsiteChat.next(data);
    }
  }

  getUserId() {
    return Crypto.decrypt(this.localStorageService.GetItem('uid'));
  }

  getOfficeId() {
    return Crypto.decrypt(this.localStorageService.GetItem('officeid'));
  }
  getNewWebSocket() {
    return webSocket(WS_ENDPOINT);
  }
  sendMessage(msg: any) {
    this.socket$?.next(msg);
  }
  close() {
    this.manualClose = true;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.closeSocketConnection();
    this.Status.next('Close');
  }
  public markReminder(id) {
    this.sendMessage({ id: id, action: 'markReminder', obj: { readed: true } });
  }
  // `setChatStatus()`, `getUserStatus()` and `manageGroup()` used to live here, sending
  // `type: 'onsite-chat'` actions. The backend confirmed the gateway never handled them — its
  // inbound handler only logged those frames — so they were no-ops on the wire and are gone.
  // Remmi Chat talks over `type: 'chat'` only; see `sendChat()` below.
  public getNoti() {
    return this.notifications2;
  }
  public getEmailRefresh() {
    return this.email_refresh;
  }
  public getEmailSyncProgress() {
    return this.email_sync_progress;
  }
  public getCalendarRefresh() {
    return this.calendar_refresh;
  }
  public getDynamicSyncBanner() {
    return this.dynamicSyncBanner;
  }

  /** Remmi Chat: server → client frames (`docs/backend/chat/internal-chat.md` §7.1). */
  public getChatEvents() {
    return this.chatEvents;
  }

  /**
   * Remmi Chat: client → server frames (§7.2) — presence changes and typing only.
   * Messages go over REST so they get status codes, retries and an idempotency key.
   *
   * The payload goes under `data`, which is what §7 defines the envelope as and what the server's
   * inbound handler reads — confirmed in `internal-chat-frontend-handover.md` §1.
   *
   * This used to send the payload flat *as well*, briefly. The flat shape was the original bug:
   * the handler read `frame.data.conversation_id`, got `undefined` on every keystroke in the
   * office and returned silently, which is the whole of why the typing indicator never once
   * appeared. Nothing caught it because `presence-update` uses this same helper and also goes over
   * `POST /chat/presence` — the REST call is what actually persists status, so a malformed socket
   * frame cost nothing visible. `typing` has no REST path, so it was the only casualty. The
   * duplicate keys were compatibility cover until the backend said which shape it reads; it has,
   * and they are gone.
   */
  public sendChat(action: string, payload: Record<string, unknown> = {}) {
    this.sendMessage({ type: 'chat', action, user: this.Client, data: { ...payload } });
  }

  shareScreen() {
    // @ts-ignore
    navigator.mediaDevices
      .getDisplayMedia()
      .then(stream => {
        this.streamRemoteVideo(stream);
      })
      .catch(_ => {});
  }

  streamRemoteVideo(stream) {
    const video = this.checkPlatformService.isBrowser() ? document.createElement('video') : null;
    video.classList.add('video');
    video.srcObject = stream;
    video.play();

    this.checkPlatformService.isBrowser() && document.getElementById('remote-video').append(video);
  }

  stopCapture() {
    clearInterval(this.capture);
  }
  startCapture() {
    this.capture = setInterval(() => {
      this.captureService
        .getImage(this.screen.nativeElement, true)
        .pipe(
          tap(img => {
            this.sendMessage({ type: 'stream', action: 'screenCapture', fromuser: this.Client, touser: '610a0f1dc61edfcf62d62bc2', data: img });
          })
        )
        .subscribe();
    }, 100);
  }
  requestCapture() {
    this.sendMessage({ type: 'stream', action: 'requestScreenCapture', fromuser: this.Client, touser: '610a0f1dc61edfcf62d62bc2' });
  }
  public getImgStream() {
    return this.imgStream;
  }

  /*********************************************/
  /* CALL SERVICE */
  /*********************************************/
  /*********************************************/

  private peer: Peer;
  private mediaCall: MediaConnection;

  private localStreamBs: BehaviorSubject<MediaStream> = new BehaviorSubject(null);
  public localStream$ = this.localStreamBs.asObservable();
  private remoteStreamBs: BehaviorSubject<MediaStream> = new BehaviorSubject(null);
  public remoteStream$ = this.remoteStreamBs.asObservable();

  private isCallStartedBs = new Subject<boolean>();
  public isCallStarted$ = this.isCallStartedBs.asObservable();

  public async _establishMediaCall(remotePeerId: string) {
    try {
      // @ts-ignore
      const stream = await navigator.mediaDevices.getDisplayMedia();

      const connection = this.peer.connect(remotePeerId);
      connection.on('error', err => {
        console.error(err);
        //this.snackBar.open(err, 'Close');
      });

      this.mediaCall = this.peer.call(remotePeerId, stream);
      if (!this.mediaCall) {
        let errorMessage = 'Unable to connect to remote peer';
        throw new Error(errorMessage);
      }
      this.localStreamBs.next(stream);
      this.isCallStartedBs.next(true);

      this.mediaCall.on('stream', remoteStream => {
        this.remoteStreamBs.next(remoteStream);
      });
      this.mediaCall.on('error', err => {
        console.error(err);
        this.isCallStartedBs.next(false);
      });
      this.mediaCall.on('close', () => this.onCallClose());
    } catch (ex) {
      console.error(ex);
      this.isCallStartedBs.next(false);
    }
  }
  public createEmptyAudioTrack = () => {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const dst: any = oscillator.connect(ctx.createMediaStreamDestination());
    oscillator.start();
    const track = dst.stream.getAudioTracks()[0];
    return Object.assign(track, { enabled: false });
  };

  public createEmptyVideoTrack = ({ width, height }) => {
    const canvas = this.checkPlatformService.isBrowser() ? Object.assign(document.createElement('canvas'), { width, height }) : null;
    canvas.getContext('2d').fillRect(0, 0, width, height);

    // @ts-ignore
    const stream = canvas.captureStream();
    const track = stream.getVideoTracks()[0];

    return Object.assign(track, { enabled: false });
  };

  public async establishMediaCall(remotePeerId: string) {
    try {
      /* create an empty stream */
      const audioTrack = this.createEmptyAudioTrack();
      const videoTrack = this.createEmptyVideoTrack({ width: 640, height: 480 });
      const stream = new MediaStream([audioTrack, videoTrack]);

      this.mediaCall = this.peer.call(remotePeerId, stream);
      if (!this.mediaCall) {
        let errorMessage = 'Unable to connect to remote peer';
        throw new Error(errorMessage);
      }
      //this.localStreamBs.next(stream);
      this.isCallStartedBs.next(true);

      this.mediaCall.on('stream', remoteStream => {
        console.info('ESTABLISH MEDIA CALL IS STEAMING NOW');
        this.callStatus.next('incall');
        this.remoteStreamBs.next(remoteStream);
      });
      this.mediaCall.on('error', err => {
        console.error(err);
        this.isCallStartedBs.next(false);
      });
      this.mediaCall.on('close', () => this.onCallClose());
    } catch (ex) {
      this.isCallStartedBs.next(false);
    }
  }

  public async _enableCallAnswer() {
    try {
      // @ts-ignore
      const stream = await navigator.mediaDevices.getDisplayMedia();
      this.localStreamBs.next(stream);
      this.peer.on('call', async call => {
        this.mediaCall = call;
        this.isCallStartedBs.next(true);

        this.mediaCall.answer(stream);
        this.mediaCall.on('stream', remoteStream => {
          this.remoteStreamBs.next(remoteStream);
        });
        this.mediaCall.on('error', err => {
          this.isCallStartedBs.next(false);
          console.error(err);
        });
        this.mediaCall.on('close', () => this.onCallClose());
      });
    } catch (ex) {
      this.isCallStartedBs.next(false);
    }
  }
  public async enableCallAnswer(callToPeerid = null) {
    try {
      console.info('i am in enable call');
      const constraints = {
        width: { min: 320, ideal: 640 },
        height: { min: 240, ideal: 480 },
        advanced: [{ width: 320, height: 240 }, { aspectRatio: 1.333 }],
      };
      // @ts-ignore
      const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
      this.localStreamBs.next(stream);
      this.peer.on('call', async call => {
        this.mediaCall = call;
        this.isCallStartedBs.next(true);

        this.mediaCall.answer(stream);
        this.mediaCall.on('stream', () => {
          console.info('enable call in steam function');
        });
        this.mediaCall.on('error', err => {
          this.isCallStartedBs.next(false);
          console.error(err);
        });
        this.mediaCall.on('close', () => this.onCallClose());
      });
      console.info('enableCall in call');
      this.sendMessage({ type: 'peerRequest', action: 'establishMediaCall', fromuser: this.PeerId, touser: callToPeerid });
    } catch (ex) {
      this.isCallStartedBs.next(false);
    }
  }

  private onCallClose() {
    this.callStatus.next('call-closed');

    if (this.remoteStreamBs?.value) {
      this.remoteStreamBs?.value.getTracks().forEach(track => {
        track.stop();
      });
    }
    if (this.localStreamBs?.value) {
      this.localStreamBs?.value.getTracks().forEach(track => {
        track.stop();
      });
    }
  }

  public closeMediaCall() {
    this.callStatus.next('call-closed');
    this.mediaCall?.close();
    if (!this.mediaCall) {
      this.onCallClose();
    }
    this.isCallStartedBs.next(false);
  }

  public destroyPeer() {
    this.manualClose = true;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.closeSocketConnection();
    this.Status.next('Close');
    this.mediaCall?.close();
    this.peer?.disconnect();
    this.peer?.destroy();
  }

  private closeSocketConnection(): void {
    if (!this.socket$) {
      return;
    }

    if (!this.socket$.closed) {
      this.socket$.complete();
    }

    this.socket$ = null;
  }
}
